import { useCallback, useState } from 'react'

/** Everything the Usage page's filter row owns: which tenants and users are
 * selected and which month is showing. The product tab is not one of them:
 * it is route state (`/usage/{tab}`), read by the page via `useProductTab`
 * so a refresh and Back/Forward restore it, and handed to `useUsagePageData`
 * alongside these filters.
 *
 * Split from `useUsagePageData` (which fetches) because these are the two
 * things that change for different reasons — this state changes when the
 * admin clicks something, the queries change when the server answers. Keeping
 * them in one hook meant every fetch concern and every input concern shared a
 * single 100-line body, and a component that only needed the filter values
 * had no way to take just those. */
export function useUsageFilters() {
  // null = every tenant/user selected (no filter) — see MultiSelect's `value` convention.
  const [tenantFilter, setTenantFilter] = useState<string[] | null>(null)
  const [userFilter, setUserFilter] = useState<string[] | null>(null)
  // null until the first response names the account's own current month
  // (spec §24b-tz) — the browser cannot compute it, since the account's
  // timezone is Mambu-side configuration rather than a browser locale.
  const [month, setMonth] = useState<string | null>(null)

  // Never feed `useState`'s setter straight to MultiSelect: it re-selects
  // everything whenever its option list changes identity, and a bare setter
  // there would make the whole filter row a new function on every render.
  const onTenantChange = useCallback((next: string[] | null) => setTenantFilter(next), [])
  const onUserChange = useCallback((next: string[] | null) => setUserFilter(next), [])

  return {
    tenantFilter,
    userFilter,
    month,
    setMonth,
    onTenantChange,
    onUserChange,
  }
}

export type UsageFilters = ReturnType<typeof useUsageFilters>
